// ROLE MATRIX — the single source of truth for what each role can see and do,
// derived from St Brigid's roles guide (spreadsheet) and reusable as the
// template for every school. Consumed by auth.js (client gating + UI hiding),
// the Roles guide, the user importer, AND encoded into firestore.rules for
// SERVER-SIDE enforcement (so "Invisible" is truly unreadable, not just hidden).
//
// Each role maps every zone to an access level:  'none' | 'view' | 'edit'.
// A zone listed in `scoped` is OWN-YEAR-LEVEL only (the yellow highlight): the
// role can only see/act on that zone for students in their own year level(s);
// other levels are invisible for THAT zone. Zones not in `scoped` are
// school-wide at the stated access level.
//
// Zone keys: student-file tabs (summary, notes, mtss=Diagnosis Triangle,
// supportTriangle=Home Triangle, specialist, assessmentData, goals,
// behaviourMonitor, fba, behaviourRating, interventions, incidents, plans,
// meetings, emails, timetable, informalChats, confidential) and capabilities
// (cap_referrals, cap_admin, cap_groupMonitoring, cap_appointments,
// cap_timetables, cap_library, cap_ai, cap_settings, cap_signatures).
//
// NOTE: the sheet's four Confidential sub-columns are collapsed to one
// `confidential` gate (all four move together in the sheet); the "Grade Levels"
// column is display-only and not modelled. Legacy role ids map in auth.js.

export const ROLE_MATRIX =
{
 "principal": {
  "sections": {
   "summary": "edit",
   "notes": "edit",
   "mtss": "edit",
   "supportTriangle": "edit",
   "specialist": "edit",
   "assessmentData": "edit",
   "goals": "edit",
   "behaviourMonitor": "edit",
   "fba": "edit",
   "behaviourRating": "edit",
   "interventions": "edit",
   "incidents": "edit",
   "plans": "edit",
   "meetings": "edit",
   "emails": "edit",
   "timetable": "edit",
   "informalChats": "edit",
   "confidential": "edit",
   "cap_referrals": "edit",
   "cap_admin": "edit",
   "cap_groupMonitoring": "edit",
   "cap_appointments": "edit",
   "cap_timetables": "edit",
   "cap_library": "edit",
   "cap_ai": "edit",
   "cap_settings": "edit",
   "cap_signatures": "edit"
  },
  "scoped": []
 },
 "ld_leader": {
  "sections": {
   "summary": "edit",
   "notes": "edit",
   "mtss": "edit",
   "supportTriangle": "edit",
   "specialist": "edit",
   "assessmentData": "edit",
   "goals": "edit",
   "behaviourMonitor": "edit",
   "fba": "edit",
   "behaviourRating": "edit",
   "interventions": "edit",
   "incidents": "edit",
   "plans": "edit",
   "meetings": "edit",
   "emails": "edit",
   "timetable": "edit",
   "informalChats": "edit",
   "confidential": "edit",
   "cap_referrals": "edit",
   "cap_admin": "edit",
   "cap_groupMonitoring": "edit",
   "cap_appointments": "edit",
   "cap_timetables": "edit",
   "cap_library": "edit",
   "cap_ai": "edit",
   "cap_settings": "edit",
   "cap_signatures": "edit"
  },
  "scoped": []
 },
 "executive": {
  "sections": {
   "summary": "edit",
   "notes": "edit",
   "mtss": "edit",
   "supportTriangle": "edit",
   "specialist": "edit",
   "assessmentData": "edit",
   "goals": "edit",
   "behaviourMonitor": "edit",
   "fba": "edit",
   "behaviourRating": "edit",
   "interventions": "edit",
   "incidents": "edit",
   "plans": "edit",
   "meetings": "edit",
   "emails": "edit",
   "timetable": "edit",
   "informalChats": "edit",
   "confidential": "edit",
   "cap_referrals": "edit",
   "cap_admin": "edit",
   "cap_groupMonitoring": "edit",
   "cap_appointments": "edit",
   "cap_timetables": "edit",
   "cap_library": "edit",
   "cap_ai": "edit",
   "cap_settings": "view",
   "cap_signatures": "edit"
  },
  "scoped": []
 },
 "ld_assistant": {
  "sections": {
   "summary": "view",
   "notes": "edit",
   "mtss": "none",
   "supportTriangle": "none",
   "specialist": "none",
   "assessmentData": "none",
   "goals": "none",
   "behaviourMonitor": "none",
   "fba": "none",
   "behaviourRating": "none",
   "interventions": "none",
   "incidents": "view",
   "plans": "none",
   "meetings": "none",
   "emails": "edit",
   "timetable": "edit",
   "informalChats": "edit",
   "confidential": "none",
   "cap_referrals": "none",
   "cap_admin": "none",
   "cap_groupMonitoring": "none",
   "cap_appointments": "edit",
   "cap_timetables": "view",
   "cap_library": "view",
   "cap_ai": "none",
   "cap_settings": "none",
   "cap_signatures": "none"
  },
  "scoped": []
 },
 "grade_teacher": {
  "sections": {
   "summary": "view",
   "notes": "edit",
   "mtss": "edit",
   "supportTriangle": "edit",
   "specialist": "view",
   "assessmentData": "edit",
   "goals": "edit",
   "behaviourMonitor": "edit",
   "fba": "edit",
   "behaviourRating": "edit",
   "interventions": "edit",
   "incidents": "edit",
   "plans": "edit",
   "meetings": "edit",
   "emails": "edit",
   "timetable": "edit",
   "informalChats": "edit",
   "confidential": "none",
   "cap_referrals": "edit",
   "cap_admin": "none",
   "cap_groupMonitoring": "edit",
   "cap_appointments": "view",
   "cap_timetables": "edit",
   "cap_library": "edit",
   "cap_ai": "none",
   "cap_settings": "none",
   "cap_signatures": "none"
  },
  "scoped": [
   "assessmentData",
   "behaviourMonitor",
   "behaviourRating",
   "cap_appointments",
   "cap_groupMonitoring",
   "cap_referrals",
   "cap_timetables",
   "emails",
   "fba",
   "goals",
   "incidents",
   "interventions",
   "meetings",
   "mtss",
   "plans",
   "specialist",
   "supportTriangle",
   "timetable"
  ]
 },
 "general_leadership": {
  "sections": {
   "summary": "view",
   "notes": "edit",
   "mtss": "view",
   "supportTriangle": "none",
   "specialist": "edit",
   "assessmentData": "none",
   "goals": "view",
   "behaviourMonitor": "edit",
   "fba": "edit",
   "behaviourRating": "view",
   "interventions": "view",
   "incidents": "edit",
   "plans": "view",
   "meetings": "none",
   "emails": "edit",
   "timetable": "none",
   "informalChats": "edit",
   "confidential": "none",
   "cap_referrals": "edit",
   "cap_admin": "none",
   "cap_groupMonitoring": "edit",
   "cap_appointments": "none",
   "cap_timetables": "view",
   "cap_library": "edit",
   "cap_ai": "none",
   "cap_settings": "none",
   "cap_signatures": "none"
  },
  "scoped": [
   "cap_groupMonitoring"
  ]
 },
 "specialist": {
  "sections": {
   "summary": "view",
   "notes": "edit",
   "mtss": "view",
   "supportTriangle": "none",
   "specialist": "edit",
   "assessmentData": "none",
   "goals": "view",
   "behaviourMonitor": "edit",
   "fba": "edit",
   "behaviourRating": "view",
   "interventions": "view",
   "incidents": "edit",
   "plans": "view",
   "meetings": "none",
   "emails": "edit",
   "timetable": "none",
   "informalChats": "edit",
   "confidential": "none",
   "cap_referrals": "edit",
   "cap_admin": "none",
   "cap_groupMonitoring": "edit",
   "cap_appointments": "none",
   "cap_timetables": "view",
   "cap_library": "edit",
   "cap_ai": "none",
   "cap_settings": "none",
   "cap_signatures": "none"
  },
  "scoped": [
   "cap_groupMonitoring"
  ]
 },
 "intervention": {
  "sections": {
   "summary": "edit",
   "notes": "edit",
   "mtss": "edit",
   "supportTriangle": "view",
   "specialist": "view",
   "assessmentData": "edit",
   "goals": "edit",
   "behaviourMonitor": "edit",
   "fba": "none",
   "behaviourRating": "view",
   "interventions": "edit",
   "incidents": "edit",
   "plans": "view",
   "meetings": "edit",
   "emails": "edit",
   "timetable": "edit",
   "informalChats": "edit",
   "confidential": "none",
   "cap_referrals": "view",
   "cap_admin": "none",
   "cap_groupMonitoring": "edit",
   "cap_appointments": "edit",
   "cap_timetables": "edit",
   "cap_library": "edit",
   "cap_ai": "edit",
   "cap_settings": "none",
   "cap_signatures": "none"
  },
  "scoped": [
   "assessmentData",
   "goals",
   "incidents",
   "mtss",
   "plans",
   "supportTriangle"
  ]
 },
 "eso_intervention": {
  "sections": {
   "summary": "view",
   "notes": "edit",
   "mtss": "view",
   "supportTriangle": "none",
   "specialist": "view",
   "assessmentData": "view",
   "goals": "view",
   "behaviourMonitor": "edit",
   "fba": "edit",
   "behaviourRating": "none",
   "interventions": "edit",
   "incidents": "edit",
   "plans": "view",
   "meetings": "none",
   "emails": "none",
   "timetable": "edit",
   "informalChats": "edit",
   "confidential": "none",
   "cap_referrals": "none",
   "cap_admin": "none",
   "cap_groupMonitoring": "edit",
   "cap_appointments": "none",
   "cap_timetables": "edit",
   "cap_library": "edit",
   "cap_ai": "none",
   "cap_settings": "none",
   "cap_signatures": "none"
  },
  "scoped": [
   "assessmentData",
   "behaviourMonitor",
   "cap_groupMonitoring",
   "cap_timetables",
   "fba",
   "goals",
   "incidents",
   "informalChats",
   "interventions",
   "mtss",
   "plans",
   "specialist",
   "timetable"
  ]
 },
 "eso": {
  "sections": {
   "summary": "view",
   "notes": "edit",
   "mtss": "view",
   "supportTriangle": "none",
   "specialist": "view",
   "assessmentData": "view",
   "goals": "view",
   "behaviourMonitor": "edit",
   "fba": "edit",
   "behaviourRating": "none",
   "interventions": "edit",
   "incidents": "edit",
   "plans": "view",
   "meetings": "none",
   "emails": "none",
   "timetable": "edit",
   "informalChats": "edit",
   "confidential": "none",
   "cap_referrals": "none",
   "cap_admin": "none",
   "cap_groupMonitoring": "edit",
   "cap_appointments": "none",
   "cap_timetables": "edit",
   "cap_library": "edit",
   "cap_ai": "none",
   "cap_settings": "none",
   "cap_signatures": "none"
  },
  "scoped": [
   "assessmentData",
   "behaviourMonitor",
   "cap_groupMonitoring",
   "cap_timetables",
   "fba",
   "goals",
   "incidents",
   "informalChats",
   "interventions",
   "mtss",
   "plans",
   "specialist",
   "timetable"
  ]
 }
}
;

// Friendly labels for the template roles (school-configurable later).
export const ROLE_TEMPLATE_LABELS = {
  principal: 'Principal / Deputy',
  ld_leader: 'Learning Diversity Leader',
  executive: 'Executive Team',
  ld_assistant: 'LD Assistant',
  grade_teacher: 'Classroom Teacher',
  general_leadership: 'General Leadership',
  specialist: 'Specialist Teacher',
  intervention: 'Intervention Teacher',
  eso_intervention: 'ESO - Intervention',
  eso: 'ESO',
};

// Human labels for each zone, for the Roles guide + editor.
export const ZONE_LABELS = {
  summary: 'Summary', notes: 'Staff Notes', mtss: 'Diagnosis Triangle',
  supportTriangle: 'Home Triangle', specialist: 'Specialist Adjustments',
  assessmentData: 'Assessments', goals: 'Goals', behaviourMonitor: 'Behaviour Monitor',
  fba: 'FBA', behaviourRating: 'Behaviour Rating', interventions: 'Interventions',
  incidents: 'Major Incidents', plans: 'Plans', meetings: 'Meetings', emails: 'Emails',
  timetable: 'Timetable', informalChats: 'Informal Chats', confidential: 'Confidential',
  cap_referrals: 'Referral forms', cap_admin: 'Admin', cap_groupMonitoring: 'Group Monitoring',
  cap_appointments: 'Appointments', cap_timetables: 'Timetables', cap_library: 'Library',
  cap_ai: 'AI Insights', cap_settings: 'Settings',
  cap_signatures: 'Sign for other staff',
};

/** Access level a role has for a zone: 'none' | 'view' | 'edit'. */
export function roleZoneAccess(role, zone) {
  return ROLE_MATRIX[role]?.sections?.[zone] || 'none';
}
/** True if the role's access to this zone is restricted to their own year level(s). */
export function roleZoneScoped(role, zone) {
  return !!ROLE_MATRIX[role]?.scoped?.includes(zone);
}

/**
 * Materialise a role's effective zone permissions for stamping onto the
 * /users doc so firestore.rules can enforce them without embedding the whole
 * matrix. Returns { zonePerms, ownLevelZones } or null for roles the matrix
 * doesn't cover (owner → full; legacy/custom handled by the caller).
 */
export function materialiseRolePerms(role) {
  if (role === 'owner') {
    const zonePerms = {};
    Object.keys(ZONE_LABELS).forEach(z => { zonePerms[z] = 'edit'; });
    return { zonePerms, ownLevelZones: [] };
  }
  const def = ROLE_MATRIX[role];
  if (!def) return null;
  return { zonePerms: { ...def.sections }, ownLevelZones: [...(def.scoped || [])] };
}
